import AlertPopup from "components/alert-popup/AlertPopup";
import InputSearch from "core/inputs/input-search/InputSearch";
import InputSelect from "core/inputs/input-select/InputSelect";
import { FORM_INFO, getContractTypeOptions } from "constants/forms";
import {
  END_DATE_AND_REASON_REQUIRED,
  ERROR_DATES,
  ERROR_ONE_CONTRACT,
  LAST_CONTRACT_LEAVING_REASON_END_DATE_REQUIRED,
  BRANCH_ID_REQUIRED,
  ERROR_AT_LEAST_ONE_CONTRACT,
} from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { phoneSize } from "utils/screenSizes";
import { useState, useEffect } from "react";
import {
  CONTRACT_TYPES,
  DATATRACK_MODELS,
  PERMANENT_END_CONTRACT_TYPES_IDS,
  POPUP_STATUS,
  QUERY_STATUS,
  SEARCH_TYPE,
  MODAL_CONTENT,
} from "constants/constants";
import {
  onAdd,
  onChange,
  onDelete,
  renderCommonBlock,
  renderStartDateEndDate,
} from "utils/tableContentHelper";
import ModalFooter from "components/modal/ModalFooter";
import _ from "lodash";
import { headerGenerator } from "utils/HeaderHelper";
import Proptypes from "prop-types";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import { updatedByDataHelper } from "utils/modalsHelper";

const ManageWorkContract = ({
  userContract,
  branches,
  reasonLeaveList,
  getUpdatedValues,
  lang,
  onClose,
  onValidate,
  setElementsToDelete,
  usersByIds,
  entityStatus,
}) => {
  const { apiErrorResponse } = useUserDatatrackContext();
  const [data, setData] = useState(userContract);
  const [status, setStatus] = useState();
  const [code, setCode] = useState();
  const [initialData, setInitialData] = useState(userContract);
  const [reloadData, setReloadData] = useState(false);

  const headerParams = [
    {
      key: "contract_type",
      defaultMessage: "Type de contrat",
      icon: "clients",
    },
    {
      key: "branch_id",
      defaultMessage: "Filiale",
      icon: "user",
      minWidth: "18rem",
    },
    {
      key: "start_date",
      defaultMessage: "Date de début de contrat",
      icon: "calendar",
    },
    {
      key: "end_date",
      defaultMessage: "Date de fin de contrat",
      icon: "calendar",
    },
    {
      key: "leaving_reason_id",
      defaultMessage: "Motif de fin de contrat",
      icon: "clients",
    },
    {
      key: "updated_by",
      defaultMessage: "Modifié par",
      icon: "user",
    },
    {
      key: "updated_at",
      defaultMessage: "Le",
      icon: "calendar",
    },
  ];

  const header = headerGenerator(headerParams);

  const hasPermanentLeavingReason = () =>
    PERMANENT_END_CONTRACT_TYPES_IDS?.includes(data?.at(-1)?.leaving_reason_id);

  useEffect(() => {
    const apiErrorCode = {
      start_date: "api_error_establishment_date",
      end_date: "api_error_end_date",
      branch_id: "api_error_branch_id",
    };

    Object.keys(apiErrorCode).forEach((field) => {
      if (apiErrorResponse?.[field]) {
        setCode(apiErrorCode[field]);
        setStatus(POPUP_STATUS.ERROR);
      }
    });
  }, [apiErrorResponse]);

  useEffect(() => {
    if (
      initialData?.length < userContract?.length &&
      userContract?.length > 0
    ) {
      setInitialData(userContract);
    }
  }, [initialData, userContract, reloadData]);

  useEffect(() => {
    if (_.isEqual(initialData, userContract) && initialData.length > 0) {
      setData(
        _.map(initialData, (elm) => ({
          ...elm,
          start_date: elm.start_date || null,
          end_date: elm.end_date || null,
        }))
      );
    }
  }, [initialData, usersByIds]);

  useEffect(() => {
    if (userContract?.length && data?.length) {
      updatedByDataHelper({ data, setData, modalData: userContract });
    }
  }, [userContract]);

  const formattedReasonLeaveList = reasonLeaveList?.map((reasonLeave) => ({
    label: lang === "fr" ? reasonLeave?.fr : reasonLeave?.en,
    value: reasonLeave?.id,
  }));

  const formattedBranchesList = branches?.map((branch) => ({
    label: branch?.name,
    value: branch?.id,
  }));

  const formatContent = (content) =>
    content.map((elm, index) => {
      const dataTestIndex = content.length - 1 - index;
      const rowBranchLabel = formattedBranchesList?.find(
        (b) => b.value === elm.branch_id
      )?.label;
      const rowContractOptions = getContractTypeOptions(
        rowBranchLabel,
        elm.contract_type
      );
      return {
        id: elm?.id,
        contract_type: (
          <InputSelect
            id="contract_type"
            value={elm.contract_type}
            options={rowContractOptions}
            name="contract_type"
            maxHeight={160}
            setValue={(name, val) =>
              onChange({
                getUpdatedValues,
                id: elm?.id,
                key: name,
                setData,
                data,
                val,
              })
            }
            dataTestId={`${MODAL_CONTENT.MANAGE_WORK_CONTRACT}_modal-contract_type-${dataTestIndex}`}
          />
        ),
        branch_id: (
          <InputSearch
            getSelectedOptions={(val) => {
              return onChange({
                getUpdatedValues,
                id: elm?.id,
                key: "branch_id",
                setData,
                data,
                val: val?.value[0]?.value,
              });
            }}
            value={formattedBranchesList.filter(
              (br) => br.value === elm.branch_id
            )}
            options={formattedBranchesList}
            limitChoice={FORM_INFO.branch.limitChoice}
            isEmpty={elm.branch_id?.length === 0}
            searchType={SEARCH_TYPE.ARRAY}
            popup={FORM_INFO.branch.popup}
            dataTestId={`${MODAL_CONTENT.MANAGE_WORK_CONTRACT}_modal-branch-${dataTestIndex}`}
          />
        ),
        ...renderStartDateEndDate({
          data,
          getUpdatedValues,
          start_date: elm?.start_date,
          end_date: elm?.end_date,
          setData,
          id: elm?.id,
          onChangeDates: onChange,
          lang,
          modalType: MODAL_CONTENT.MANAGE_WORK_CONTRACT,
          index,
        }),
        leaving_reason_id: (
          <InputSelect
            id="leaving_reason_id"
            value={elm.leaving_reason_id}
            options={[{ label: "", value: "" }, ...formattedReasonLeaveList]}
            name="leaving_reason_id"
            maxHeight={160}
            setValue={(name, val) =>
              onChange({
                getUpdatedValues,
                id: elm?.id,
                key: name,
                setData,
                data,
                val,
              })
            }
            dataTestId={`${MODAL_CONTENT.MANAGE_WORK_CONTRACT}_modal-leaving_reason-${dataTestIndex}`}
          />
        ),
        ...renderCommonBlock({
          data,
          getUpdatedValues,
          id: elm.id,
          onDelete,
          setData,
          updated_at: elm.updated_at,
          updated_by: elm.updated_by,
          lang,
          setElementsToDelete,
          deleteError: data?.filter((e) => !e.new_row)?.length - 1 === 0,
          errorToDisplay: () => setError(ERROR_AT_LEAST_ONE_CONTRACT),
          deleteBtnTestId: `${MODAL_CONTENT.MANAGE_WORK_CONTRACT}_modal-delete_btn-${dataTestIndex}`,
        }),
      };
    });

  const setError = (code) => {
    setCode(code);
    setStatus(POPUP_STATUS.ERROR);
    setReloadData(!reloadData);
  };

  const isNotCDIOrMandate = (contract_type) =>
    contract_type !== CONTRACT_TYPES.CDI &&
    contract_type !== CONTRACT_TYPES.REPRESENTATIVE;

  const isLastContractAndMissingEndOrReason = (
    contracts,
    end_date,
    leaving_reason_id
  ) => contracts.length > 0 && (!end_date || !leaving_reason_id);

  const isOverlappingDates = (previousEndDate, start_date) =>
    previousEndDate && start_date <= previousEndDate;

  const checkContracts = (contracts) => {
    let previousEndDate = null;
    const sortedContracts = [...contracts].sort((a, b) =>
      (a.start_date || "").localeCompare(b.start_date || "")
    );

    for (const contract of sortedContracts) {
      const {
        start_date,
        end_date,
        leaving_reason_id,
        contract_type,
        branch_id,
        newRow: new_row = false,
      } = contract || {};

      const isLastContract =
        contract?.id === sortedContracts[sortedContracts?.length - 1]?.id;

      if (!branch_id) return BRANCH_ID_REQUIRED;

      if (!new_row && !isLastContract && (!end_date || !leaving_reason_id))
        return END_DATE_AND_REASON_REQUIRED;

      if (
        (isLastContract || new_row) &&
        (isNotCDIOrMandate(contract_type) || end_date || leaving_reason_id) &&
        isLastContractAndMissingEndOrReason(
          contracts,
          end_date,
          leaving_reason_id
        )
      )
        return LAST_CONTRACT_LEAVING_REASON_END_DATE_REQUIRED;

      if (
        isOverlappingDates(previousEndDate, start_date) ||
        !start_date ||
        (end_date && end_date < start_date)
      )
        return ERROR_DATES;

      previousEndDate = end_date;
    }

    return null;
  };

  const formattedData = (data) =>
    data?.map((e) => ({
      contract_type: e.contract_type,
      branch_id: e.branch_id,
      start_date: e.start_date,
      end_date: e.end_date,
      leaving_reason_id: e.leaving_reason_id,
    }));

  const onClickValidate = () => {
    const error = checkContracts(data);
    if (error) {
      setError(error);
      return;
    }

    const hasDataChanges =
      JSON.stringify(formattedData(initialData)) !==
      JSON.stringify(formattedData(data));

    return hasDataChanges && onValidate();
  };

  const onAddChecker = ({ header, data, setData }) => {
    if (!data.find((elm) => elm?.newRow)) {
      onAdd({ header, data, setData, type: DATATRACK_MODELS.CONTRACT });
    } else {
      setError(ERROR_ONE_CONTRACT);
    }
  };

  return (
    <>
      <button
        onClick={() => {
          onAddChecker({ header, data, setData });
        }}
        style={{ all: "unset", marginBottom: "2rem" }}
        disabled={hasPermanentLeavingReason()}
        data-testid={`${MODAL_CONTENT.MANAGE_WORK_CONTRACT}_modal-add_contract_btn`}
      >
        <Typography
          className={`link-1 ${hasPermanentLeavingReason() ? "" : "violet"}`}
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_work_contract"
                defaultMessage="Ajouter un contrat"
              />
            </>
          }
        />
      </button>
      <br />
      <div></div>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="contract"
        header={header}
        content={data?.length > 0 ? formatContent(data) : []}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
      <br />
      <br />
      <ModalFooter
        onClose={onClose}
        onValidate={() => onClickValidate()}
        disabled={entityStatus === QUERY_STATUS.LOADING}
        modalType={MODAL_CONTENT.MANAGE_WORK_CONTRACT}
      />
    </>
  );
};

export default ManageWorkContract;

ManageWorkContract.propTypes = {
  userContract: Proptypes.array,
  branches: Proptypes.array,
  reasonLeaveList: Proptypes.array,
  getUpdatedValues: Proptypes.func,
  lang: Proptypes.string,
  onClose: Proptypes.func,
  onValidate: Proptypes.func,
  setElementsToDelete: Proptypes.func,
  usersByIds: Proptypes.array,
  entityStatus: Proptypes.string,
};
